import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const UpcomingInterviews = ({ 
  interviews, 
  onReschedule, 
  onCancel, 
  onSendReminder, 
  onEdit 
}) => {
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');

  const interviewTypes = {
    'phone': { color: 'bg-blue-100 text-blue-800 border-blue-200', icon: 'Phone' },
    'video': { color: 'bg-green-100 text-green-800 border-green-200', icon: 'Video' },
    'in-person': { color: 'bg-purple-100 text-purple-800 border-purple-200', icon: 'MapPin' }
  };

  const statusColors = {
    'scheduled': 'bg-success/10 text-success border-success/20',
    'confirmed': 'bg-primary/10 text-primary border-primary/20',
    'pending': 'bg-warning/10 text-warning border-warning/20',
    'cancelled': 'bg-destructive/10 text-destructive border-destructive/20',
    'completed': 'bg-muted text-muted-foreground border-border'
  };

  const filterOptions = [
    { value: 'all', label: 'All Interviews' },
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'This Week' },
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'confirmed', label: 'Confirmed' }
  ];

  const sortOptions = [
    { value: 'date', label: 'Date & Time' },
    { value: 'candidate', label: 'Candidate Name' },
    { value: 'interviewer', label: 'Interviewer' },
    { value: 'type', label: 'Interview Type' }
  ];

  const getFilteredInterviews = () => {
    let filtered = [...interviews];

    // Apply filters
    if (filter === 'today') {
      const today = new Date()?.toDateString();
      filtered = filtered?.filter(interview => 
        new Date(interview.dateTime)?.toDateString() === today
      );
    } else if (filter === 'week') {
      const now = new Date();
      const weekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
      filtered = filtered?.filter(interview => {
        const interviewDate = new Date(interview.dateTime);
        return interviewDate >= now && interviewDate <= weekFromNow;
      });
    } else if (filter !== 'all') {
      filtered = filtered?.filter(interview => interview?.status === filter);
    }

    // Apply sorting
    filtered?.sort((a, b) => {
      switch (sortBy) {
        case 'date':
          return new Date(a.dateTime) - new Date(b.dateTime);
        case 'candidate':
          return a?.candidateName?.localeCompare(b?.candidateName);
        case 'interviewer':
          return a?.interviewerName?.localeCompare(b?.interviewerName);
        case 'type':
          return a?.type?.localeCompare(b?.type);
        default:
          return 0;
      }
    });

    return filtered;
  };

  const formatDateTime = (dateTime) => {
    const date = new Date(dateTime);
    return {
      date: date?.toLocaleDateString('en-US', { 
        weekday: 'short', 
        month: 'short', 
        day: 'numeric' 
      }),
      time: date?.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      })
    };
  };

  const isUpcoming = (dateTime) => {
    return new Date(dateTime) > new Date();
  };

  const getTimeUntil = (dateTime) => {
    const now = new Date();
    const interviewDate = new Date(dateTime);
    const diffMs = interviewDate - now;
    
    if (diffMs < 0) return 'Past';
    
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? 's' : ''}`;
    if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? 's' : ''}`;
    return 'Soon';
  };

  const filteredInterviews = getFilteredInterviews();

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-secondary/10 rounded-lg flex items-center justify-center">
            <Icon name="Clock" size={18} color="var(--color-secondary)" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Upcoming Interviews</h2>
            <p className="text-sm text-muted-foreground">
              {filteredInterviews?.length} interview{filteredInterviews?.length !== 1 ? 's' : ''} scheduled
            </p>
          </div>
        </div>
      </div>
      {/* Filters and Sort */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1">
          <select
            value={filter}
            onChange={(e) => setFilter(e?.target?.value)}
            className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          >
            {filterOptions?.map(option => (
              <option key={option?.value} value={option?.value}>
                {option?.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex-1">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e?.target?.value)}
            className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          >
            {sortOptions?.map(option => (
              <option key={option?.value} value={option?.value}>
                Sort by {option?.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      {/* Interviews List */}
      <div className="space-y-3 max-h-96 overflow-y-auto">
        {filteredInterviews?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="Calendar" size={48} className="mx-auto text-muted-foreground mb-3" />
            <h3 className="text-lg font-medium text-foreground mb-2">No Interviews Found</h3>
            <p className="text-muted-foreground">
              {filter === 'all' ?'No interviews scheduled yet.' 
                : `No interviews match the selected filter: ${filterOptions?.find(f => f?.value === filter)?.label}`
              }
            </p>
          </div>
        ) : (
          filteredInterviews?.map((interview) => {
            const { date, time } = formatDateTime(interview?.dateTime);
            const typeConfig = interviewTypes?.[interview?.type];
            const upcoming = isUpcoming(interview?.dateTime);
            
            return (
              <div
                key={interview?.id}
                className="bg-surface border border-border rounded-lg p-4 hover:shadow-card-elevation-hover transition-all duration-200"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {/* Interview Header */}
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                        <Icon name="User" size={18} color="var(--color-primary)" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-foreground">{interview?.candidateName}</h3>
                        <p className="text-sm text-muted-foreground">
                          with {interview?.interviewerName}
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full border ${statusColors?.[interview?.status]}`}>
                          {interview?.status?.charAt(0)?.toUpperCase() + interview?.status?.slice(1)}
                        </span>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full border ${typeConfig?.color}`}>
                          <Icon name={typeConfig?.icon} size={12} className="inline mr-1" />
                          {interview?.type?.charAt(0)?.toUpperCase() + interview?.type?.slice(1)}
                        </span>
                      </div>
                    </div>

                    {/* Interview Details */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="flex items-center space-x-2 text-sm">
                        <Icon name="Calendar" size={14} color="var(--color-muted-foreground)" />
                        <span className="text-foreground font-medium">{date}</span>
                      </div>
                      <div className="flex items-center space-x-2 text-sm">
                        <Icon name="Clock" size={14} color="var(--color-muted-foreground)" />
                        <span className="text-foreground font-medium">{time}</span>
                        <span className="text-muted-foreground">({interview?.duration} min)</span>
                      </div>
                      {interview?.location && (
                        <div className="flex items-center space-x-2 text-sm">
                          <Icon name="MapPin" size={14} color="var(--color-muted-foreground)" />
                          <span className="text-muted-foreground">{interview?.location}</span>
                        </div>
                      )}
                      {interview?.meetingLink && (
                        <div className="flex items-center space-x-2 text-sm">
                          <Icon name="Link" size={14} color="var(--color-muted-foreground)" />
                          <a 
                            href={interview?.meetingLink} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-primary hover:underline"
                          >
                            Join Meeting
                          </a>
                        </div>
                      )}
                    </div>

                    {/* Time Until */}
                    {upcoming && (
                      <div className="flex items-center space-x-2 text-sm mb-4">
                        <Icon name="Timer" size={14} color="var(--color-accent)" />
                        <span className="text-accent font-medium">
                          {getTimeUntil(interview?.dateTime)} away
                        </span>
                      </div>
                    )}

                    {/* Notes */}
                    {interview?.notes && (
                      <div className="bg-muted/50 rounded-lg p-3 mb-4">
                        <p className="text-sm text-muted-foreground">{interview?.notes}</p>
                      </div>
                    )}
                  </div>
                </div>
                {/* Actions */}
                <div className="flex items-center justify-end space-x-2 pt-3 border-t border-border">
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Edit"
                    onClick={() => onEdit(interview)}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Calendar"
                    onClick={() => onReschedule(interview)}
                    disabled={!upcoming}
                  >
                    Reschedule
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Mail"
                    onClick={() => onSendReminder(interview)}
                    disabled={!upcoming}
                  >
                    Reminder
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    iconName="X"
                    onClick={() => onCancel(interview)}
                    disabled={!upcoming}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default UpcomingInterviews;